import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { characters } from '@/mocks/characters';
import { usePageMeta } from '@/hooks/usePageMeta';

type Tab = 'movie' | 'character';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') ?? '').trim();
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<Tab>('movie');

  usePageMeta(
    q ? `搜索「${q}」- 玉女心经2` : '搜索影视与角色 - 玉女心经2',
    '支持影视作品与角色双重搜索，输入关键词即可查找剧情、人物与背景资料。'
  );

  useEffect(() => {
    setInput(q);
  }, [q]);

  const kw = q.toLowerCase();
  const movieResults = q
    ? movies.filter((m) =>
        [m.title, m.subtitle, m.synopsis, m.director, m.region, m.category, ...m.genres, ...m.cast]
          .join(' ')
          .toLowerCase()
          .includes(kw)
      )
    : [];
  const charResults = q
    ? characters.filter((c) =>
        [c.name, c.actor, c.roleTag, c.background, c.personality].join(' ').toLowerCase().includes(kw)
      )
    : [];

  const recommend = [...movies].sort((a, b) => b.views - a.views).slice(0, 3);
  const hasQuery = q.length > 0;
  const totalResults = movieResults.length + charResults.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
    setTab('movie');
  };

  const activeResults = tab === 'movie' ? movieResults : charResults;

  return (
    <div className="w-full bg-background-50 px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <div className="mt-5 max-w-3xl">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">搜索影视与角色</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          同时检索影视作品与角色资料，输入片名、类型、演员或角色名即可快速定位内容。
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-4 py-3 focus-within:border-primary-400 transition-colors">
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            placeholder="例如：玉女心经、武侠、林清鸢、反派…"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
          />
          {input && (
            <button
              type="button"
              onClick={() => setInput('')}
              className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer"
              aria-label="清空"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
        <button
          type="submit"
          className="rounded-md bg-accent-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {hasQuery && (
        <p className="mt-5 text-sm text-foreground-600">
          当前搜索词：<span className="font-medium text-foreground-900">「{q}」</span>
          <span className="ml-2 text-foreground-500">共找到 {totalResults} 条结果</span>
        </p>
      )}

      {hasQuery && (
        <div className="mt-5 flex items-center rounded-full bg-background-200/70 px-1 py-1 w-fit">
          <button
            type="button"
            onClick={() => setTab('movie')}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
              tab === 'movie' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-700'
            }`}
          >
            <i className="ri-film-line"></i> 影视作品 ({movieResults.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('character')}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
              tab === 'character' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-700'
            }`}
          >
            <i className="ri-user-3-line"></i> 角色 ({charResults.length})
          </button>
        </div>
      )}

      {hasQuery && activeResults.length > 0 && (
        <div className="mt-6">
          {tab === 'movie' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
              {movieResults.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {charResults.map((c) => (
                <Link
                  key={c.id}
                  to={`/character/${c.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 cursor-pointer"
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-background-200">
                    <img
                      src={c.avatar}
                      alt={`${c.name} 角色头像`}
                      title={`${c.name} · ${c.actor} 饰`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-2 top-2 rounded bg-primary-900/85 px-2 py-0.5 font-label text-[10px] text-background-100">
                      {c.roleTag}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {c.name}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">演员 · {c.actor}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 空状态 / 未搜索提示 */}
      {(!hasQuery || totalResults === 0) && (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/80 bg-background-100 px-6 py-12 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className={hasQuery ? 'ri-search-eye-line text-3xl' : 'ri-search-line text-3xl'}></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              {hasQuery ? '没有找到匹配的内容' : '输入关键词开始搜索'}
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              {hasQuery
                ? '换个关键词试试，比如片名、类型、演员或角色名；也可以先看看下面的热门推荐。'
                : '支持搜索影视作品与角色，例如「玉女心经」「武侠」「花间令」等。'}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {['玉女心经2', '武侠', '悬疑', '花间令'].map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setSearchParams({ q: word });
                    setTab('movie');
                  }}
                  className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <h2 className="font-heading text-xl font-black text-foreground-950">热门推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
              {recommend.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}